/**
 * NERB (Net Eligible Receivables Balance) Time Series Screen
 *
 * Displays the total NERB over time -- the aggregate borrowing-base-eligible
 * receivables after all exclusions (cross-aging, concentration limits,
 * ineligibles) have been applied. Each row is a reference-date snapshot.
 *
 * Data flow:
 *  - Reads `data.nerb` and `project.hasNerb` from Redux.
 *  - "Calculate" dispatches `calculateNerb` to the Python backend, which
 *    iterates over all reference dates and applies the full eligibility
 *    waterfall (buyer parent AR -> concentration limits -> NERB).
 *  - Unlike ConcentrationLimits (single-date), this produces the full
 *    historical time series for trend analysis.
 */
import React, { useState } from "react";
import { useDispatch } from "react-redux";
// -------------------------------------------------------------
import { calculateNerb, getNerbData } from "Redux/data/actions";
import { useAnalysis, useScenarioFlag } from "Redux/data/hooks";
import DataTable from "Components/DataTable";
import SkeletonTable from "Components/Skeleton";

const headers = [
  { text: "Reference Date", key: "Reference Date", datatype: "date" },
  { text: "Total NERB", key: "Total NERB", datatype: "float" },
];

// -------------------------------------------------------------
// -------------------------------------------------------------
const Nerb = () => {
  const dispatch = useDispatch();
  const [calculating, setCalculating] = useState(false);

  const { data: nerb, has: hasNerb } = useAnalysis("nerb", getNerbData);
  const hasMaster = useScenarioFlag("hasMaster");

  if (!hasMaster)
    return (
      <div className="notification is-warning is-light">
        No master frame has been built yet.
      </div>
    );

  return (
    <div>
      <div className="box" style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <button
          className={`button is-small ${calculating ? "is-loading" : ""}`}
          onClick={() => {
            setCalculating(true);
            dispatch(calculateNerb(() => setCalculating(false)));
          }}
        >
          {hasNerb ? "Recalculate" : "Calculate"} NERB Time Series
        </button>
      </div>
      {hasNerb && !nerb && <SkeletonTable />}
      {nerb && (
        <DataTable
          data={nerb.data}
          headers={headers}
          reference="Reference Date"
        />
      )}
    </div>
  );
};

export default Nerb;
